import { Deferred, Head, router } from "@inertiajs/react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CheckIcon, ChevronDownIcon, ExternalLinkIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	EMPTY_PIC_SLOTS,
	PicSlotSelectors,
	type PicSlots,
} from "@/components/admin-portal/appointment/form/pic-slot-selectors";
import { PageContainer } from "@/components/admin-portal/shared/page-layout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import {
	MultiSelector,
	MultiSelectorContent,
	MultiSelectorInput,
	MultiSelectorItem,
	MultiSelectorList,
	MultiSelectorTrigger,
} from "@/components/ui/multi-select";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { getCsrfToken } from "@/lib/csrf";
import {
	type AddressMatch,
	activePackages,
	addressCandidatesFor,
	addressChoiceValue,
	chooseAddress,
	choosePatient,
	type DecisionKey,
	type Decisions,
	decidedLocationId,
	decisionsPayload,
	groupIssuesBySeverity,
	hasBlockingIssues,
	type Issue,
	isDecided,
	issuesAfterDecisions,
	type LocationOption,
	markSourceLines,
	NEW_CHOICE,
	NO_DECISIONS,
	type PackageOption,
	type ParsedData,
	type PatientMatch,
	packagesByVisitMatch,
	patientChoiceValue,
	pendingDecisions,
	reconcileDecisions,
	requestedVisitCount,
	type ServiceOption,
	type Severity,
	type SourceLine,
	servicesForLocation,
	WA_CONDITIONS,
	withSuggestedCondition,
} from "@/lib/drafts/wa-decisions";
import { cn } from "@/lib/utils";

// Mirrors the parser's floor (ParseWATextRequest: `min=20`) so the page does not fire a
// request the backend is going to reject anyway.
const MIN_TEXT_LENGTH = 20;
// Governs the first reading only — the one that fires on its own after a paste. Long
// enough that a paste-then-tidy does not fire three reads, short enough that the result
// still feels like a reaction. Every later reading is asked for by hand.
const DEBOUNCE_MS = 600;

interface PreferredSchedule {
	date: string;
	timePreference: string;
}

interface Preview {
	data: ParsedData;
	issues: Issue[] | null;
	// Echoed back on save so the backend's duplicate guard compares the exact text that
	// was read, and the draft records which parser produced it.
	rawTextHash?: string;
	parserVersion?: string;
	unmatchedLines: string[] | null;
	matches?: {
		patients: PatientMatch[] | null;
		addresses: AddressMatch[] | null;
	} | null;
	options?: {
		packages: PackageOption[] | null;
		locations: LocationOption[] | null;
	} | null;
	// Absent until fisiohome-backend#245 ships; the page must work without it.
	suggestedDecisions?: { condition?: string | null } | null;
}

// A 409 from the backend, already turned into something readable by the controller. The
// link is optional on purpose: the clashing identity only exists inside the backend's
// error sentence, so a reworded message costs the link, never the explanation.
interface Conflict {
	kind: string;
	message: string;
	link?: { url: string; label: string };
}

interface AdminOption {
	id: string;
	name: string;
	email: string;
}

interface PageProps {
	// Every active service plus the cities it reaches, so the page can offer only the ones
	// delivered where the visit actually lands. Deferred, like the admins.
	services?: ServiceOption[];
	// Everyone but the admin doing the pasting — they are the draft's PIC already.
	// Optional because it is deferred: absent on the first paint, present once Inertia has
	// fetched it.
	admins?: AdminOption[];
	// The admin doing the pasting, whom `admins` deliberately leaves out. The KPI slots
	// need them back: L1 defaults to them, and holding L1 and L3 at once is the ordinary
	// "made it and closed it myself" case.
	currentAdmin?: AdminOption;
	// Governs the three KPI slot selectors — see issue #131.
	draftPicKpiEnabled?: boolean;
	previewPath: string;
	createPath: string;
}

// The backend warns about an identical text at preview time; a duplicate patient it can
// only find while writing. So this is the one clash an admin can answer before saving.
const DUPLICATE_TEXT_CODE = "DUPLICATE_SUBMISSION";

// Which parsed fields each template section shows, in template order. Kept as data rather
// than as JSX so the four sections render through one component.
const SECTIONS = [
	{
		key: "patient",
		fields: [
			"fullName",
			"gender",
			"dateOfBirth",
			"age",
			"birthPlace",
			"complaint",
			"illnessOnset",
			"condition",
			"medicalHistory",
		],
	},
	{
		key: "address",
		fields: [
			"street",
			"kelurahan",
			"kecamatan",
			"cityText",
			"postalCode",
			"landmark",
			"mapsLink",
			"locationCity",
			"coordinateSource",
		],
	},
	{
		key: "scheduling",
		fields: ["preferredSchedules", "numberOfVisit", "preferredTherapistGender"],
	},
	{
		key: "contact",
		fields: [
			"contactName",
			"contactPhone",
			"email",
			"referralSource",
			"customReferralSource",
		],
	},
] as const;

// Which part of the reading answers which decision. The four section cards used to sit
// below the whole form, so judging "is this the right patient?" meant scrolling past every
// other decision and back. Each step now carries only the fields that decide it.
const STEP_FIELDS: Partial<Record<DecisionKey, readonly string[]>> = {
	// The PIC's name and email sit here rather than in their own section: the template's
	// "Data PIC" block is how an admin recognises the patient when the patient's own name is
	// spelled differently, so it belongs beside the candidate list, not two cards away.
	patient: [
		"fullName",
		"gender",
		"dateOfBirth",
		"age",
		"contactName",
		"contactPhone",
		"email",
	],
	address: ["street", "kelurahan", "kecamatan", "cityText", "postalCode"],
	city: ["cityText", "locationCity"],
	// Only the visit count. The complaint and the preferred dates matter to the booking, not
	// to picking which package sells that many visits — they read below with the rest.
	service: ["numberOfVisit"],
	condition: ["condition"],
};

// Field names are unique across the four sections, which is what lets a step name the
// fields it wants without also naming where they live.
const FIELD_SECTION = new Map(
	SECTIONS.flatMap((section) =>
		section.fields.map((field) => [field as string, section.key] as const),
	),
);

// The referral's free-text half is rendered inside the referral row itself, so it must not
// also get a row of its own — see FieldGrid.
const MERGED_INTO_REFERRAL = "customReferralSource";

// Whatever no step claimed, grouped — read occasionally, acted on rarely. Folded away at the
// foot rather than dropped: the page must still show everything it parsed. One flat grid of
// eleven unrelated values was a wall; these are the two clusters an admin actually looks
// something up in, plus a remainder.
//
// Listed in the order they read, not in template order: within a group the sequence is the
// one that makes sense to a person, not the one the parser happened to emit.
const REST_GROUPS = [
	{
		key: "patient",
		fields: ["birthPlace", "complaint", "illnessOnset", "medicalHistory"],
	},
	{ key: "address", fields: ["landmark", "mapsLink", "coordinateSource"] },
] as const;

// The remainder is computed, never listed: a field added to the parser that no step and no
// group above claims still lands on the page instead of vanishing silently.
const REST_OTHER_FIELDS = SECTIONS.flatMap(
	(section) => section.fields as readonly string[],
).filter(
	(field) =>
		field !== MERGED_INTO_REFERRAL &&
		!Object.values(STEP_FIELDS).some((fields) => fields.includes(field)) &&
		!REST_GROUPS.some((group) =>
			(group.fields as readonly string[]).includes(field),
		),
);

const SEVERITY_VARIANT: Record<Severity, "destructive" | "accent" | "outline"> =
	{
		error: "destructive",
		warning: "accent",
		info: "outline",
	};

// The one repeated value the parser returns: the preferred visit dates, in the order the
// template asks for them ("tanggal kunjungan pilihan 1", "pilihan 2"). One string per
// choice, so a caller can print them as rows or run them together into a sentence.
function scheduleLines(value: unknown): string[] {
	if (!Array.isArray(value)) return [];

	return (value as PreferredSchedule[])
		.map((schedule) => {
			if (!schedule?.date) return "";
			return schedule.timePreference
				? `${schedule.date} — ${schedule.timePreference}`
				: schedule.date;
		})
		.filter(Boolean);
}

// The pasted text is whatever the patient typed, so a "maps link" may well not be a link at
// all. Only an http(s) URL is worth making clickable; anything else is shown as the text it
// is, never as a dead anchor.
function httpUrl(value: unknown): string | null {
	if (typeof value !== "string" || !value.trim()) return null;
	try {
		const url = new URL(value.trim());
		return url.protocol === "http:" || url.protocol === "https:"
			? url.toString()
			: null;
	} catch {
		return null;
	}
}

// The slice of the reading a single decision rests on. A description list rather than
// stacked paragraphs: these are label/value pairs, and dt/dd is what lets a screen reader
// announce them as pairs instead of as eight loose sentences.
function FieldGrid({
	fields,
	data,
	format,
}: {
	fields: readonly string[];
	data: Record<string, Record<string, unknown>>;
	format: (value: unknown) => string;
}) {
	const { t } = useTranslation("appointments");

	// Three fields do not read as plain text. Kept as a switch inside the one component that
	// renders them rather than as a renderer registry — three cases do not need a mechanism.
	const renderValue = (field: string, value: unknown) => {
		switch (field) {
			// A maps link is long enough to break the two-column grid on its own, so the URL
			// itself is never printed — the link carries a label and the whole address in its
			// tooltip. Anything that is not an http(s) URL is shown as the text it is: the
			// patient typed it, and a dead anchor would only invite a click that goes nowhere.
			case "mapsLink": {
				const href = httpUrl(value);
				const raw = typeof value === "string" ? value.trim() : "";
				if (!href && !raw) return format(value);
				if (!href) return raw;

				// New tab, like the conflict link below: leaving this page unmounts it and the
				// pasted text goes with it. The icon says so on sight; the sr-only half says it
				// to a screen reader, which an icon alone never reaches.
				return (
					<a
						href={href}
						target="_blank"
						rel="noopener noreferrer"
						// Native title rather than a Tooltip: this is a value to read, not a
						// control, and a hover card inside a folded card is more machinery than
						// the payoff deserves.
						title={raw}
						className="inline-flex items-center max-w-full gap-1 text-sm font-medium underline rounded-sm text-primary underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
					>
						<span className="truncate">
							{t("drafts.paste_wa_form.fields.maps_link_open")}
						</span>

						<ExternalLinkIcon aria-hidden className="size-3 shrink-0" />

						<span className="sr-only">
							{t("drafts.paste_wa_form.fields.maps_link_new_tab")}
						</span>
					</a>
				);
			}
			// A bare "8" beside "Jumlah Kunjungan" reads as a code. The unit is carried in the
			// value, and `visits` rather than `count` because i18next treats a variable named
			// count as a plural trigger and would look for keys that do not exist.
			case "numberOfVisit":
				return typeof value === "number"
					? t("drafts.paste_wa_form.fields.visit_value", { visits: value })
					: format(value);
			// Each preferred date is a separate answer, so they read as rows. Run together
			// behind commas they became one long string an admin had to take apart by eye to
			// see how many choices there even were.
			case "preferredSchedules": {
				const lines = scheduleLines(value);
				if (!lines.length) return format(value);

				return (
					<ul className="list-disc list-inside marker:text-muted-foreground">
						{lines.map((line, index) => (
							// Positional by nature: the template asks for them in order, and the
							// same date can legitimately be asked for twice (DATE_DUPLICATE is a
							// finding, not a rejection), so the position is the only stable key.
							<li key={`${index}-${line}`}>{line}</li>
						))}
					</ul>
				);
			}
			// The template's referral answer is two fields — the picked option and the free
			// text behind "Lainnya" — that only ever mean anything together.
			case "referralSource": {
				const custom = data.contact?.[MERGED_INTO_REFERRAL];
				const parts = [value, custom]
					.filter((part) => part !== null && part !== undefined && part !== "")
					.map(String);
				return parts.length ? parts.join(" • ") : format(value);
			}
			default:
				return format(value);
		}
	};

	return (
		<dl className="grid gap-x-4 gap-y-2 p-3 border rounded-md bg-muted/30 sm:grid-cols-2">
			{fields.map((field) => {
				const section = FIELD_SECTION.get(field);

				return (
					<div key={field} className="min-w-0 space-y-0.5">
						<dt className="text-xs text-muted-foreground">
							{t(`drafts.paste_wa_form.fields.${field}`)}
						</dt>

						{/* Read-only on purpose: the text is the source of truth, so a wrong
						    value is fixed in the textarea and read again. */}
						<dd className="text-sm break-words">
							{renderValue(field, section ? data[section]?.[field] : undefined)}

							{/* A date of birth derived from "Usia" lands on 1 January and
							    otherwise reads like a real one. Marked on the value itself. */}
							{field === "dateOfBirth" &&
							data.patient?.dateOfBirthIsEstimated ? (
								<span className="ml-1 text-xs text-muted-foreground">
									{t("drafts.paste_wa_form.fields.dob_estimated")}
								</span>
							) : null}
						</dd>
					</div>
				);
			})}
		</dl>
	);
}

type StepState = "done" | "open" | "waiting";

// One numbered decision on the rail. A settled step shrinks to its answer plus a way back
// in, so the page gets shorter as the admin works; the step being waited on is the only one
// showing a control. Later steps stay visible and say what they are waiting for rather than
// appearing out of nowhere — a step an admin cannot see coming is one they cannot plan for.
function DecisionStep({
	index,
	last,
	label,
	state,
	summary,
	waitingText,
	editLabel,
	onEdit,
	closeLabel,
	onClose,
	children,
}: {
	index: number;
	last?: boolean;
	label: string;
	state: StepState;
	summary?: string;
	waitingText?: string;
	editLabel: string;
	onEdit: () => void;
	closeLabel: string;
	// Set only while this step is the one the admin reopened by hand. Without it, keeping the
	// same answer is not an answer a radio group can report — Radix fires no change when the
	// selected option is clicked again — so the step had no way to close and the rail stalled.
	onClose?: () => void;
	children: React.ReactNode;
}) {
	// The 6px travel is decoration; the crossfade is what tells the admin the step changed.
	// So reduced-motion keeps the fade and drops the movement, rather than cutting both.
	const reduceMotion = useReducedMotion();
	const shift = reduceMotion ? 0 : -6;

	return (
		<section
			className={cn(
				"relative pl-10",
				// The rail is drawn from each numeral down into the gap below it, so the five
				// steps read as one sequence rather than five unrelated blocks.
				!last &&
					"before:absolute before:left-[13px] before:top-8 before:-bottom-5 before:w-px before:bg-border",
			)}
		>
			<span
				className={cn(
					"absolute left-0 top-0 grid border rounded-full size-7 place-items-center text-xs font-medium tabular-nums transition-all duration-200",
					state === "done"
						? "border-primary/40 text-primary"
						: state === "open"
							? "bg-primary text-primary-foreground border-primary"
							: "text-muted-foreground",
				)}
			>
				{state === "done" ? <CheckIcon className="size-3.5" /> : index}
			</span>

			<div className="flex flex-wrap items-center justify-between gap-2 min-h-7">
				<Label className="uppercase">{label}</Label>

				{state === "done" ? (
					<Button
						type="button"
						variant="ghost-primary"
						size="xs"
						onClick={onEdit}
					>
						{editLabel}
					</Button>
				) : state === "open" && onClose ? (
					<Button
						type="button"
						variant="ghost-primary"
						size="xs"
						onClick={onClose}
					>
						{closeLabel}
					</Button>
				) : null}
			</div>

			{/* Keyed on the state so leaving and arriving are two separate elements: the body
			    of a step that has just been answered fades out while its one-line summary
			    fades in, instead of the text swapping in place. mode="wait" because both
			    occupy the same row — overlapping them would double the step's height
			    mid-transition and shove the rest of the rail down.

			    Same primitive and the same 0.3s ease-out as the filter panel on the Drafts
			    page, so disclosure feels like one thing across the admin portal. */}
			<AnimatePresence initial={false} mode="wait">
				<motion.div
					key={state}
					initial={{ opacity: 0, y: shift }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0, y: shift }}
					transition={{ duration: 0.3, ease: "easeOut" }}
				>
					{state === "done" ? (
						<p className="text-sm font-medium text-pretty">{summary}</p>
					) : state === "waiting" ? (
						<p className="text-xs text-muted-foreground text-pretty">
							{waitingText}
						</p>
					) : (
						<div className="mt-2 space-y-3">{children}</div>
					)}
				</motion.div>
			</AnimatePresence>
		</section>
	);
}

// One selectable candidate. The whole row is the label so the click target is the card,
// not the 16px circle.
function OptionRow({
	value,
	id,
	children,
}: {
	value: string;
	id: string;
	children: React.ReactNode;
}) {
	return (
		<Label
			htmlFor={id}
			className={cn(
				// bg-input rather than the card's own background: an unpicked option should
				// look like a control waiting for an answer, not like a paragraph.
				// transition-all rather than transition-colors: picking a row moves its border
				// colour, its background, and — on the row that was picked before — nothing
				// else, so there is no layout property here for `all` to make jump.
				"flex items-start gap-3 p-3 font-normal border rounded-md cursor-pointer bg-input hover:bg-muted/50 transition-all duration-200",
				// Radix marks the item, not this wrapper, so the row reads its own child's
				// state. `has-` needs Tailwind 3.4+, which this project is on.
				"has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5",
			)}
		>
			<RadioGroupItem value={value} id={id} className="mt-1 shrink-0" />

			<div className="min-w-0 space-y-1">{children}</div>
		</Label>
	);
}

// The one place on the page that asks an admin to step over a guard. Both moments live in
// the same frame — the duplicate the preview warned about, armed before the save, and the
// clash only the write could find, answered after it — so the two read as one idea in two
// tenses rather than as a switch and a button that happen to do the same thing.
//
// The left rule is the whole signal: nothing else on this page carries one, so a panel with
// it means "you are about to overrule something on purpose".
function OverridePanel({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}) {
	return (
		<section className="p-3 space-y-2 border border-l-4 rounded-md border-l-destructive/60 bg-destructive/5">
			<p className="text-sm font-medium">{title}</p>

			{children}
		</section>
	);
}

// The matcher's suggestions, capped and scrollable. The "buat baru" option is deliberately
// left outside this box by every caller: a text matching eight patients would otherwise push
// the one choice that is always available off the bottom of the step.
//
// Native overflow rather than the ScrollArea component, matching SourceReader above — the
// padding pair gives a focused radio's ring room to draw instead of being clipped.
function CandidateList({ children }: { children: React.ReactNode }) {
	return (
		<div className="p-1 -m-1 space-y-2 overflow-y-auto max-h-64">
			{children}
		</div>
	);
}

// The pasted text once it has been read: line-numbered, read-only, with the lines the
// parser could not read marked where they sit. The lines it did read are left plain on
// purpose — a page where every row is coloured says nothing about which row needs a
// second look, and an unread line is the only one an admin can act on.
function SourceReader({
	lines,
	unmatchedLabel,
}: {
	lines: SourceLine[];
	unmatchedLabel: string;
}) {
	return (
		<div className="overflow-y-auto border rounded-md max-h-96 bg-background">
			{lines.map((line, index) => (
				<div
					// Identical lines are normal in a pasted template, so position is the
					// only thing that identifies a row.
					// biome-ignore lint/suspicious/noArrayIndexKey: lines are not unique
					key={index}
					className={cn(
						"flex gap-2 px-2 py-0.5 font-mono text-xs border-l-2",
						line.matched
							? "border-l-transparent"
							: "border-l-secondary bg-secondary/5",
					)}
				>
					<span className="w-6 text-right shrink-0 tabular-nums text-muted-foreground">
						{index + 1}
					</span>

					<span
						className={cn(
							"min-w-0 break-words",
							line.matched ? "" : "text-secondary",
						)}
					>
						{/* A blank line still needs to occupy its row, or the numbering
						    stops matching what the admin sees in the textarea. */}
						{line.text || " "}
						{!line.matched ? (
							<span className="sr-only"> — {unmatchedLabel}</span>
						) : null}
					</span>
				</div>
			))}
		</div>
	);
}

export default function DraftPasteWaForm({
	services,
	admins,
	currentAdmin,
	draftPicKpiEnabled,
	previewPath,
	createPath,
}: PageProps) {
	const { t } = useTranslation("appointments");
	const [rawText, setRawText] = useState("");
	// The text the reading on screen was made from. It trails `rawText` while an admin edits
	// an already-read paste: those edits only reach the parser when they press "Read again",
	// so nothing shown — or saved — is ever attributed to text the parser never saw.
	const [readText, setReadText] = useState("");
	const [preview, setPreview] = useState<Preview | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [reading, setReading] = useState(false);
	const [decisions, setDecisions] = useState<Decisions>(NO_DECISIONS);
	// A settled step the admin has asked to see again. Null means the rail decides on its
	// own, which is the normal state — this only ever holds a step reopened by hand.
	const [editingStep, setEditingStep] = useState<DecisionKey | null>(null);
	const [patientCandidates, setPatientCandidates] = useState<PatientMatch[]>(
		[],
	);
	// A textarea cannot mark its own lines, so the source has two modes: the textarea while
	// the admin is still typing, and a read-only line-numbered view once there is a reading
	// to mark it against. Editing anything sends it straight back to the textarea.
	const [editingText, setEditingText] = useState(true);
	// The reading the decisions on screen were made against. A ref rather than a read off
	// `preview`, which a failed read clears: the decisions outlive that failure, so what
	// they were judged against has to outlive it too.
	const decidedAgainstRef = useRef<ParsedData | null>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	// Raised when the edit was asked for from the read-only side of the page. The textarea
	// does not exist until the flip has rendered, so the focus has to wait for it rather
	// than happen with it.
	const pendingTextFocusRef = useRef(false);
	// Optional, and deliberately not part of `pendingDecisions`: a draft with no extra PIC
	// is a complete draft, so leaving this empty must never hold the save button.
	const [adminPicIds, setAdminPicIds] = useState<string[]>([]);
	// L1 pre-filled with whoever is pasting, which is what the server falls back to anyway
	// — spelled out here so the admin can see it and hand it to someone else.
	const [picSlots, setPicSlots] = useState<PicSlots>({
		...EMPTY_PIC_SLOTS,
		picL1: currentAdmin?.id ?? null,
	});
	// Armed by the admin against a duplicate they were shown, never a default.
	const [allowDuplicate, setAllowDuplicate] = useState(false);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [conflict, setConflict] = useState<Conflict | null>(null);

	// The one way into the text, wherever it was asked for — the button over the source, or
	// a section heading on the right. Centred rather than merely focused: the source column
	// is sticky and the sections that send an admin here sit well below its top, so a bare
	// focus() leaves the caret in a textarea that is off screen.
	// Stable so the effect below can depend on it without re-running every render — it
	// reads refs only, so it has nothing to close over.
	const focusSourceText = useCallback(() => {
		textareaRef.current?.focus({ preventScroll: true });
		textareaRef.current?.scrollIntoView({ block: "center" });
	}, []);

	const editSourceText = () => {
		if (editingText) {
			focusSourceText();
			return;
		}
		pendingTextFocusRef.current = true;
		setEditingText(true);
	};

	useEffect(() => {
		if (!editingText || !pendingTextFocusRef.current) return;
		pendingTextFocusRef.current = false;
		focusSourceText();
	}, [editingText, focusSourceText]);

	// Every decision goes through here so answering a reopened step hands the rail back to
	// the page. Without it, a step reopened by hand would stay open after it was answered.
	const decide = (next: (current: Decisions) => Decisions) => {
		setDecisions(next);
		setEditingStep(null);
	};

	// What an edit to the text invalidates on its own — which is not the decisions. Whether
	// a decision still stands is a question only the next reading can answer, and it is
	// answered there, per decision, by `reconcileDecisions`. Dropping all five here is what
	// made an admin re-answer the patient, the address, the city, the service and the
	// package after fixing a typo in the complaint.
	const invalidateSubmission = () => {
		setSaveError(null);
		// Edited text is a different submission, so the conflict it clashed with — and the
		// permission to override it — is no longer the one being answered. The PIC picks
		// survive: who follows this up was never derived from the text.
		setConflict(null);
		setAllowDuplicate(false);
	};

	// The only array the parser returns is the preferred-schedule pairing; everything else
	// is a scalar. Kept narrow on purpose rather than pretending to be a general formatter.
	const formatField = useCallback(
		(value: unknown): string => {
			if (value === null || value === undefined || value === "") {
				return t("drafts.na");
			}
			if (Array.isArray(value)) {
				const lines = scheduleLines(value);
				return lines.length ? lines.join(", ") : t("drafts.na");
			}
			return String(value);
		},
		[t],
	);

	// Set once the admin has settled on an existing patient: the parser then stops proposing
	// patients and narrows the address candidates to that patient's own addresses.
	const decidedPatientId =
		decisions.patient?.mode === "existing" ? decisions.patient.patientId : null;
	// Re-reading with a service is what makes the parser answer with that service's
	// packages and resolve the "8x" in the text against them.
	const decidedServiceId = decisions.serviceId;

	// Only the very first reading fires on its own. Keyed on `preview` so a failed read —
	// which clears it — falls back to auto as well: there is no reading on screen to
	// protect, and the admin is still in "just get it read" mode.
	useEffect(() => {
		if (preview) return;
		setReadText(rawText);
	}, [rawText, preview]);

	useEffect(() => {
		const text = readText.trim();
		if (text.length < MIN_TEXT_LENGTH) {
			setPreview(null);
			setError(null);
			setReading(false);
			return;
		}

		// Aborting on cleanup is what drops answers to superseded reads: the effect re-runs
		// on every edit, so an in-flight parse of older text never reaches setState.
		const controller = new AbortController();
		setReading(true);

		const timer = setTimeout(async () => {
			try {
				const res = await fetch(previewPath, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						"X-CSRF-Token": getCsrfToken(),
					},
					body: JSON.stringify({
						raw_text: readText,
						service_id: decidedServiceId,
						patient_id: decidedPatientId,
					}),
					signal: controller.signal,
				});
				const body = await res.json();

				if (res.ok && body.success) {
					const next = body.data as Preview;

					// A read scoped to a decided patient stops proposing patients at all, so the
					// candidate list is only ever taken from an unscoped read — otherwise the
					// card the admin just picked would disappear the moment it was picked.
					if (!decidedPatientId) {
						setPatientCandidates(next.matches?.patients ?? []);
					}

					// Keep every decision the new reading does not contradict. When it does
					// contradict the patient, clearing it makes `decidedPatientId` null, which
					// re-runs this effect into an unscoped read — the one that brings back
					// candidates for the name now in the text. No loop: the effect is keyed on
					// those two ids, not on the decisions object.
					setDecisions((current) =>
						withSuggestedCondition(
							reconcileDecisions(
								current,
								decidedAgainstRef.current,
								next.data,
								next.options?.packages,
							),
							next.suggestedDecisions,
						),
					);
					decidedAgainstRef.current = next.data;

					setPreview(next);
					setError(null);
					// Only a successful reading can mark the text, so this is the one place
					// the source is allowed to leave the textarea.
					setEditingText(false);
				} else {
					// Back to the textarea: a failed read leaves nothing to mark the lines
					// with, and the text is the only thing the admin can act on from here.
					setEditingText(true);
					// Never leave the last good preview on screen next to a failed read —
					// that is the state an admin mistakes for "it saved".
					setPreview(null);
					setError(body.error || t("drafts.paste_wa_form.read_failed"));
				}
			} catch (err) {
				if ((err as Error).name === "AbortError") return;
				setEditingText(true);
				setPreview(null);
				setError(t("drafts.paste_wa_form.read_failed"));
			} finally {
				// Not after an abort: a newer read is already in flight and has set this
				// back to true, so clearing it here would hide that it is still working.
				if (!controller.signal.aborted) setReading(false);
			}
		}, DEBOUNCE_MS);

		return () => {
			clearTimeout(timer);
			controller.abort();
		};
	}, [readText, decidedServiceId, decidedPatientId, previewPath, t]);

	// The parser emits one UNKNOWN_LABEL finding per unrecognised line, which would print
	// the unmatched-lines card a second time inside the findings list. Dropped here rather
	// than in the proxy: the backend still records them, and the dedicated card below is
	// where an admin reads them.
	// Read after the decisions, so a blank condition the admin has since answered stops
	// being listed as what holds the save.
	const decidedIssues = issuesAfterDecisions(preview?.issues, decisions);
	const issues = decidedIssues.filter(
		(issue) => issue.code !== "UNKNOWN_LABEL",
	);
	const unmatchedLines = preview?.unmatchedLines ?? [];
	// Marked against the text that produced this reading — `readText`, never `rawText`:
	// unread edits would shift every line under marks the parser made for other text.
	const sourceLines = markSourceLines(readText, unmatchedLines);
	// Edits made since the last reading. Only meaningful once there is one: before that,
	// `readText` is still catching up to the paste and this would only flash.
	const staleText = !!preview && rawText !== readText;
	// The floor the parser enforces, so "Read again" is dead rather than rejected.
	const tooShort = rawText.trim().length < MIN_TEXT_LENGTH;
	// The switch only exists next to a duplicate the admin can actually see. Without this,
	// it would be a standing invitation to disarm a guard nobody has tripped.
	const duplicateWarned = issues.some(
		(issue) => issue.code === DUPLICATE_TEXT_CODE,
	);

	// Undefined until the deferred prop lands. Nothing can be picked before then, so an
	// empty list is the honest reading of "not here yet".
	const adminOptions = admins ?? [];
	// The slots may name the acting admin; the multi-select above may not, because the
	// draft already has them as its PIC and one person cannot be listed twice.
	const picSlotAdmins = currentAdmin
		? [currentAdmin, ...adminOptions]
		: adminOptions;
	// Read from the unfiltered candidate list rather than the one the address radio group
	// renders: both hold the same rows, but this one does not also depend on the patient
	// decision the city has already been settled against.
	const locationId = decidedLocationId(decisions, preview?.matches?.addresses);
	const serviceOptions = servicesForLocation(services, locationId);
	// Why the service picker is dead, in the order the admin can act on it — the same rule
	// the save button follows: a disabled control whose reason is hidden is one an admin
	// retries instead of acting on. Null means it is usable.
	const serviceDisabledReason = !decisions.address
		? t("drafts.paste_wa_form.decisions.service.needs_address")
		: locationId === null
			? t("drafts.paste_wa_form.decisions.service.needs_city")
			: // Still in flight: the list is deferred, so it lands a moment after the page.
				services === undefined
				? t("drafts.paste_wa_form.decisions.service.loading")
				: !serviceOptions.length
					? t("drafts.paste_wa_form.decisions.service.empty")
					: null;
	const addressCandidates = addressCandidatesFor(
		decisions,
		preview?.matches?.addresses,
	);
	const cityCandidates = preview?.options?.locations ?? [];
	// The template states a visit count, so the package that sells exactly that many leads
	// the list and says why — leads, not gets picked: the parser reads "8x" from prose that
	// can also say "8x dulu, nanti nambah", and this page never decides on the admin's behalf.
	const requestedVisits = requestedVisitCount(preview?.data);
	const packages = packagesByVisitMatch(
		activePackages(preview?.options?.packages),
		requestedVisits,
	);
	const visitMatched =
		requestedVisits !== null &&
		packages.some((option) => option.numberOfVisit === requestedVisits);
	const pending = pendingDecisions(decisions);

	// The rail, in the order the page asks for it. A stop can cover more than one decision:
	// service and package are one choice made in two controls — the package list only exists
	// once a service is picked, so two numbered stops for it was one stop too many.
	//
	// City is a stop only for a new address, listed conditionally rather than skipped so the
	// numbering never has a hole in it.
	const steps: DecisionKey[][] = [
		["patient"],
		["address"],
		...(decisions.address?.mode === "new" ? [["city"] as DecisionKey[]] : []),
		["service", "package"],
		["condition"],
	];
	const stepIndex = (key: DecisionKey) =>
		steps.findIndex((stop) => stop.includes(key)) + 1;
	// Settled is asked of the decision itself, never of `pending`. That list is ordered and
	// leaves out what cannot be asked yet, so an address with no patient is absent from it —
	// reading absence as "done" ticked off steps nobody made and offered a way back into a
	// step that would not reopen. See isDecided.
	const stopSettled = (stop: DecisionKey[]) =>
		stop.every((key) => isDecided(decisions, key));
	// A stop reopened by hand wins over the next one due; otherwise the page is on the first
	// thing still pending. Null once nothing is left, which collapses the whole rail.
	// Only a settled step can be held open by hand — the "Ubah" button that sets this is the
	// only way in, and it only exists on a settled step. Re-checked here rather than trusted
	// because a step can stop being settled while it is open (choosing a different patient
	// drops the address), and a held-open step nothing can answer is a dead end.
	const openStep =
		(editingStep && isDecided(decisions, editingStep) ? editingStep : null) ??
		pending[0] ??
		null;
	// A stop is settled only when every decision on it is, so the merged one stays open
	// through the package even though the service under it is already answered.
	const stepState = (key: DecisionKey): StepState => {
		const stop = steps.find((candidate) => candidate.includes(key)) ?? [key];
		if (openStep && stop.includes(openStep)) return "open";
		return stopSettled(stop) ? "done" : "waiting";
	};
	const settledCount = steps.filter(stopSettled).length;

	// What a settled step collapses to. Read from the same lists the controls render from,
	// so a summary can never name an option the admin was never actually offered.
	const stepSummary = (key: DecisionKey): string => {
		const na = t("drafts.na");

		switch (key) {
			case "patient": {
				if (!decisions.patient) return na;
				if (decisions.patient.mode === "new") {
					return t("drafts.paste_wa_form.decisions.patient.new");
				}
				const id = decisions.patient.patientId;
				const chosen = patientCandidates.find((c) => c.patientId === id);
				return chosen ? `${chosen.name} · ${chosen.patientNumber}` : na;
			}
			case "address": {
				if (!decisions.address) return na;
				if (decisions.address.mode === "new") {
					return t("drafts.paste_wa_form.decisions.address.new");
				}
				const id = decisions.address.patientAddressId;
				return (
					addressCandidates.find((c) => c.patientAddressId === id)?.address ??
					na
				);
			}
			case "city":
				return (
					cityCandidates.find((o) => o.id === decisions.locationId)?.city ?? na
				);
			case "service":
				return (
					serviceOptions.find((o) => o.id === decisions.serviceId)?.name ?? na
				);
			case "package": {
				const chosen = packages.find((o) => o.id === decisions.packageId);
				return chosen ? `${chosen.name} (${chosen.numberOfVisit}x)` : na;
			}
			case "condition":
				return conditionLabel(decisions.condition) ?? na;
		}
	};

	// Labelled from the booking form's option, so the WA page and the booking form name a
	// condition the same way.
	const conditionLabel = (code: string | null) => {
		const value = WA_CONDITIONS.find((option) => option.code === code)?.value;
		return value
			? t(`form.update_medical_record.condition_options.${value}.label`)
			: null;
	};

	const percent = (score: number) => `${Math.round(score * 100)}%`;

	// Read from the unfiltered list on purpose: the findings card hides UNKNOWN_LABEL, but
	// a heavy finding blocks whether or not it is on screen.
	const blocked = hasBlockingIssues(decidedIssues);
	const canSave =
		!!preview && !blocked && !staleText && pending.length === 0 && !saving;
	// Why the button is dead, in the order the admin can act on it. Never left implicit —
	// a disabled button with no reason is indistinguishable from a broken one.
	const blockedReason = staleText
		? t("drafts.paste_wa_form.read.stale")
		: blocked
			? t("drafts.paste_wa_form.save.blocked_issues")
			: pending.length
				? t("drafts.paste_wa_form.save.blocked_pending")
				: null;

	// Two ways in, both deliberate: the switch the admin arms against a duplicate the
	// preview warned about, and the button inside the conflict alert for a clash only the
	// write could find. The argument wins over the switch so answering a conflict does not
	// depend on a setState that has not landed yet.
	const save = async (override = allowDuplicate) => {
		if (!preview || !canSave) return;

		setSaving(true);
		setSaveError(null);
		setConflict(null);

		try {
			const res = await fetch(createPath, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					"X-CSRF-Token": getCsrfToken(),
				},
				body: JSON.stringify({
					raw_text: readText,
					raw_text_hash: preview.rawTextHash,
					parser_version: preview.parserVersion,
					// Sent back as it was shown: the fields are read-only, so this is exactly
					// what the admin approved.
					data: preview.data,
					// Echoed for audit — the backend stores what the parser doubted.
					issues: preview.issues ?? [],
					decisions: decisionsPayload(decisions),
					// Omitted when empty rather than sent as []: nothing extra to record is
					// not the same request as "record an empty PIC list".
					...(adminPicIds.length ? { admin_pic_ids: adminPicIds } : {}),
					// The KPI slots, applied by Rails after fisiohome-backend has written the
					// draft — it knows nothing about them yet. Ignored while the flag is off.
					...(draftPicKpiEnabled
						? {
								pic_l1: picSlots.picL1,
								pic_l2: picSlots.picL2,
								pic_l3: picSlots.picL3,
							}
						: {}),
					...(override ? { allow_duplicate: true } : {}),
				}),
			});
			const body = await res.json();

			if (res.ok && body.success) {
				// Inertia visit rather than a location change, so the flash naming what was
				// written to master data arrives with the booking form.
				router.visit(body.redirectTo);
				return;
			}

			// A conflict is shown as its own thing rather than as a red failure: it has a way
			// out, and the plain error alert has no room for one.
			if (body.conflict) {
				setConflict(body.conflict as Conflict);
			} else {
				setSaveError(body.error || t("drafts.paste_wa_form.save.failed"));
			}
		} catch {
			setSaveError(t("drafts.paste_wa_form.save.failed"));
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
			<Head title={t("drafts.paste_wa_form.head_title")} />

			<PageContainer className="min-h-[100vh] flex-1 md:min-h-min space-y-4">
				<div>
					<h1 className="text-lg font-bold tracking-tight uppercase">
						{t("drafts.paste_wa_form.page_title")}
					</h1>

					<p className="w-full text-sm text-muted-foreground text-pretty md:max-w-[60ch]">
						{t("drafts.paste_wa_form.page_description")}
					</p>
				</div>

				<Separator className="bg-border" />

				<div className="grid gap-4 lg:grid-cols-2 lg:items-start">
					{/* The text and what the parser doubted about it, stacked. Sticky as one
					    column so both stay beside the decisions they explain — top-16 clears
					    the sticky AppTopBar, matching what therapist-selection and the schedule
					    calendar already offset by. */}
					<div className="space-y-4 lg:sticky lg:top-16">
						<Card>
							<CardContent className="pt-6 space-y-4">
								<div className="space-y-2">
									<div className="flex flex-wrap items-center justify-between gap-2">
										<Label htmlFor="wa-text">
											{t("drafts.paste_wa_form.text_label")}
										</Label>

										{!editingText ? (
											<Button
												type="button"
												variant="outline"
												size="sm"
												onClick={editSourceText}
											>
												{t("drafts.paste_wa_form.source.edit")}
											</Button>
										) : null}
									</div>

									{editingText ? (
										<>
											<Textarea
												ref={textareaRef}
												id="wa-text"
												rows={18}
												className="font-mono text-xs"
												placeholder={t("drafts.paste_wa_form.text_placeholder")}
												value={rawText}
												onChange={(event) => {
													setRawText(event.target.value);
													invalidateSubmission();
												}}
											/>

											{/* Manual from the second reading on: a correction typed into an
											    already-read paste is sent when the admin says so, not a moment
											    after they stop typing. Said out loud rather than left to the
											    dead save button, because the edit is on this side of the page
											    and the button is on the other. */}
											{preview ? (
												<div className="flex flex-wrap items-center justify-between gap-2">
													<Button
														type="button"
														size="sm"
														onClick={() => setReadText(rawText)}
														disabled={!staleText || reading || tooShort}
													>
														{t("drafts.paste_wa_form.read.again")}
													</Button>

													{staleText ? (
														<span className="text-xs font-medium text-yellow-600 dark:text-yellow-500">
															{tooShort
																? t("drafts.paste_wa_form.read.too_short")
																: t("drafts.paste_wa_form.read.stale")}
														</span>
													) : null}
												</div>
											) : null}
										</>
									) : (
										<>
											<SourceReader
												lines={sourceLines}
												unmatchedLabel={t(
													"drafts.paste_wa_form.source.unmatched_label",
												)}
											/>

											{/* Only what the parser could not read. An "x of y lines read"
										    badge sat beside this one and was dropped: it is this same
										    number subtracted from the line total, so it said nothing new
										    and read as a score the reading was not actually being given. */}
											{unmatchedLines.length ? (
												<Badge variant="accent">
													{t("drafts.paste_wa_form.source.unmatched_count", {
														count: unmatchedLines.length,
													})}
												</Badge>
											) : null}
										</>
									)}

									<p className="text-xs text-muted-foreground">
										{editingText
											? t("drafts.paste_wa_form.text_hint")
											: unmatchedLines.length
												? t("drafts.paste_wa_form.unmatched.description")
												: t("drafts.paste_wa_form.source.all_read")}
									</p>
								</div>
							</CardContent>
						</Card>

						{/* Hidden when there is nothing to report: a card that only ever says
						    "no findings" is scroll an admin pays for and learns nothing from.
						    The clean state is a chip in the action bar instead. */}
						{preview && issues.length ? (
							<Card>
								<CardHeader className="pb-3">
									<CardTitle className="text-sm uppercase">
										{t("drafts.paste_wa_form.issues.title")}
									</CardTitle>
								</CardHeader>

								{/* Capped and scrollable, like the source panel above it. Both live in a
								    sticky column, and a sticky column taller than the viewport is one
								    whose bottom can never be scrolled to. */}
								<CardContent className="space-y-4 overflow-y-auto max-h-80">
									{groupIssuesBySeverity(issues).map(
										(group, groupIndex, groups) => (
											<section key={group.severity} className="space-y-2">
												{/* The severity is said once per group rather than stamped on every
											    row — the colour of the rule already repeats it — and it is said
											    together with what it means for the save button, which is the only
											    question an admin actually has about a finding. */}
												<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
													<p className="text-xs font-medium uppercase">
														{t(
															`drafts.paste_wa_form.severity.${group.severity}`,
														)}
													</p>

													<Badge
														variant={SEVERITY_VARIANT[group.severity]}
														className="px-1.5 py-0 font-normal rounded-full tabular-nums"
													>
														{group.issues.length}
													</Badge>

													<p className="w-full text-xs text-muted-foreground text-pretty">
														{t(
															`drafts.paste_wa_form.issues.consequence.${group.severity}`,
														)}
													</p>
												</div>

												<ol className="space-y-2">
													{group.issues.map((issue, index) => (
														// Indexed on purpose: several findings share a code and carry an
														// empty field (form-level), so nothing in an issue is unique.
														<li
															// biome-ignore lint/suspicious/noArrayIndexKey: no stable id in the payload
															key={index}
															className={cn(
																"flex gap-2 pl-2 border-l-2",
																group.severity === "error"
																	? "border-l-destructive"
																	: group.severity === "warning"
																		? "border-l-accent-foreground/40"
																		: "border-l-border",
															)}
														>
															{/* Numbered across the whole card, not restarted per group, so
														    "temuan 4" names one finding rather than three. Counted from
														    the groups before this one — the rendered order is the order
														    the number has to follow, and that order is the grouping's,
														    not the backend's. */}
															<span className="w-4 text-xs text-right shrink-0 text-muted-foreground tabular-nums mt-0.5">
																{groups
																	.slice(0, groupIndex)
																	.reduce(
																		(total, prior) =>
																			total + prior.issues.length,
																		0,
																	) +
																	index +
																	1}
															</span>

															<div className="min-w-0 space-y-1">
																{/* The Indonesian sentence comes from the backend — there is
															    deliberately no code-to-text table on this side. */}
																<p className="text-sm text-pretty">
																	{issue.messageId}
																</p>

																{/* The snippet the finding came from, quoted rather than
															    printed flat: the line-numbered text sits directly above in
															    this same column, and quote marks are what say "find this up
															    there" without pretending to know which line it was. */}
																{issue.rawValue ? (
																	<p className="font-mono text-xs break-words text-muted-foreground">
																		&ldquo;{issue.rawValue}&rdquo;
																	</p>
																) : null}
															</div>
														</li>
													))}
												</ol>
											</section>
										),
									)}
								</CardContent>
							</Card>
						) : null}
					</div>

					<div className="space-y-4">
						{error ? (
							<Alert variant="destructive">
								<AlertTitle>{t("drafts.paste_wa_form.error_title")}</AlertTitle>
								<AlertDescription>{error}</AlertDescription>
							</Alert>
						) : null}

						{reading ? (
							<p className="text-sm text-muted-foreground">
								{t("drafts.paste_wa_form.reading")}
							</p>
						) : null}

						{!preview && !error && !reading ? (
							<Card>
								<CardContent className="py-12 space-y-2 text-sm text-center text-muted-foreground">
									<p>{t("drafts.paste_wa_form.empty")}</p>

									{/* The reading fires on its own a moment after the paste. Said out
									    loud because nothing on screen implies it — an admin who expects a
									    button waits for one. */}
									<p className="mx-auto text-xs text-pretty max-w-[46ch]">
										{t("drafts.paste_wa_form.empty_hint")}
									</p>
								</CardContent>
							</Card>
						) : null}

						{preview ? (
							<>
								{/* Sticky rather than at the foot of the column: the reading below can
								    run several screens, and a save button an admin has to go looking
								    for is one they cannot tell apart from a broken one.

								    Everything conditional was moved out from under it. A sticky element
								    has to have a bounded height — with the clash alert and the override
								    inside, this card grew past 300px and then held that much of the
								    viewport for the rest of the session. What is left is three fixed
								    rows: how far along, the one button, and why it is dead. */}
								<Card className="sticky z-10 top-16">
									<CardContent className="pt-6 space-y-3">
										{/* How far along the rail is, and what the parser doubted. A bar rather
										    than a count of what is left: "four of five" is read at a glance,
										    "1 keputusan tersisa" has to be turned into one. */}
										<div className="space-y-1.5">
											<div className="flex items-center justify-between gap-2">
												<p className="text-xs text-muted-foreground">
													{t("drafts.paste_wa_form.status.progress", {
														done: settledCount,
														total: steps.length,
													})}
												</p>

												<Badge
													variant={
														blocked
															? "destructive"
															: issues.length
																? "accent"
																: "outline"
													}
													className="px-1.5 py-0 font-normal rounded-full"
												>
													{issues.length
														? t("drafts.paste_wa_form.status.findings", {
																count: issues.length,
															})
														: t("drafts.paste_wa_form.status.clean")}
												</Badge>
											</div>

											<Progress
												// Hidden from the reading order rather than labelled: the
												// sentence directly above it already says the same count, and
												// a labelled progressbar here would announce it twice.
												aria-hidden
												className="h-1.5"
												value={(settledCount / steps.length) * 100}
											/>
										</div>

										{/* Full width at every size rather than sharing a row with the bar.
										    The label is six words — beside a bar it either overflowed the
										    column or forced the bar down to nothing. It is also the page's
										    single destination, so the width is honest about that. */}
										{/* The write creates master data — a patient, an address — off a text
										    someone else typed. One deliberate stop before the POST is cheap next
										    to unpicking a patient created twice. The decisions are not repeated
										    inside it: they are on screen behind the dialog, and a summary that
										    drifts from them is worse than none. The conflict panel below keeps
										    its own direct button — answering a clash is already a deliberate
										    act, and a second dialog would ask the same question twice. */}
										<AlertDialog>
											<AlertDialogTrigger asChild>
												<Button
													type="button"
													className="w-full"
													disabled={!canSave}
												>
													{saving
														? t("drafts.paste_wa_form.save.saving")
														: t("drafts.paste_wa_form.save.label")}
												</Button>
											</AlertDialogTrigger>

											<AlertDialogContent>
												<AlertDialogHeader>
													<AlertDialogTitle>
														{t("drafts.paste_wa_form.save.confirm.title")}
													</AlertDialogTitle>

													<AlertDialogDescription>
														{t("drafts.paste_wa_form.save.confirm.description")}
													</AlertDialogDescription>
												</AlertDialogHeader>

												<AlertDialogFooter>
													<AlertDialogCancel>
														{t("drafts.paste_wa_form.save.confirm.cancel")}
													</AlertDialogCancel>

													{/* Wrapped, not passed by reference: onClick hands the click
													    event to its first argument, and a MouseEvent is truthy — the
													    ordinary save would have sent allow_duplicate on every press. */}
													<AlertDialogAction onClick={() => save()}>
														{t("drafts.paste_wa_form.save.confirm.submit")}
													</AlertDialogAction>
												</AlertDialogFooter>
											</AlertDialogContent>
										</AlertDialog>

										{/* The reason sits under the button rather than in a tooltip: a
										    disabled control with a hidden explanation is what makes an admin
										    retry instead of fixing the text. */}
										{blockedReason ? (
											<p className="text-xs text-muted-foreground text-pretty">
												{blockedReason}
											</p>
										) : null}
									</CardContent>
								</Card>

								{/* Out of the sticky card and into the scroll, all three keyed so they
								    cross-fade in rather than shoving the decisions down without warning. */}
								<AnimatePresence initial={false}>
									{saveError ? (
										<motion.div
											key="save-error"
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											exit={{ opacity: 0 }}
											transition={{ duration: 0.2, ease: "easeOut" }}
										>
											<Alert variant="destructive">
												<AlertTitle>
													{t("drafts.paste_wa_form.save.failed_title")}
												</AlertTitle>
												<AlertDescription>{saveError}</AlertDescription>
											</Alert>
										</motion.div>
									) : null}

									{/* The clash the write found, the row it clashed with when the message
									    still named one, and the deliberate way through — never a default,
									    always a click of its own. */}
									{conflict ? (
										<motion.div
											key="conflict"
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											exit={{ opacity: 0 }}
											transition={{ duration: 0.2, ease: "easeOut" }}
										>
											<OverridePanel
												title={t("drafts.paste_wa_form.conflict.title")}
											>
												<p className="text-sm text-pretty">
													{conflict.message}
												</p>

												{/* New tab, not an Inertia visit: navigating away unmounts this
												    page and the pasted text goes with it, so checking what
												    clashed would cost the admin the paste. */}
												{conflict.link ? (
													<a
														href={conflict.link.url}
														target="_blank"
														rel="noopener noreferrer"
														className="inline-block text-sm font-medium underline rounded-sm underline-offset-4 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
													>
														{conflict.link.label}
													</a>
												) : null}

												<p className="text-xs text-muted-foreground text-pretty">
													{t("drafts.paste_wa_form.conflict.description")}
												</p>

												<Button
													type="button"
													variant="destructive-outline"
													size="sm"
													className="w-full sm:w-auto"
													disabled={saving}
													onClick={() => save(true)}
												>
													{saving
														? t("drafts.paste_wa_form.conflict.continuing")
														: t("drafts.paste_wa_form.conflict.continue")}
												</Button>
											</OverridePanel>
										</motion.div>
									) : null}

									{/* Armed ahead of the save, against a duplicate the preview actually
									    reported — and hidden once the write came back with a clash, which
									    the panel above answers directly. One switch disarms two separate
									    guards on the backend, so the description names both. */}
									{duplicateWarned && !conflict ? (
										<motion.div
											key="duplicate"
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											exit={{ opacity: 0 }}
											transition={{ duration: 0.2, ease: "easeOut" }}
										>
											<OverridePanel
												title={t("drafts.paste_wa_form.duplicate.label")}
											>
												<p className="text-xs text-muted-foreground text-pretty">
													{t("drafts.paste_wa_form.duplicate.description")}
												</p>

												<Label
													htmlFor="wa-allow-duplicate"
													className="flex items-center gap-3 p-2 -m-2 font-normal rounded-md cursor-pointer transition-colors duration-200 hover:bg-destructive/5"
												>
													<Switch
														id="wa-allow-duplicate"
														checked={allowDuplicate}
														onCheckedChange={setAllowDuplicate}
														disabled={saving}
														className="shrink-0"
													/>

													<span className="text-sm">
														{t("drafts.paste_wa_form.duplicate.confirm")}
													</span>
												</Label>
											</OverridePanel>
										</motion.div>
									) : null}
								</AnimatePresence>

								{/* The only editable part of the page. Everything above is what the template
									    said; this is what the template never says and a machine must not guess. */}
								<Card>
									<CardHeader className="pb-3">
										<CardTitle className="text-sm uppercase">
											{t("drafts.paste_wa_form.decisions.title")}
										</CardTitle>
									</CardHeader>

									<CardContent className="space-y-6 text-sm">
										<p className="text-xs text-muted-foreground text-pretty">
											{t("drafts.paste_wa_form.decisions.description")}
										</p>

										{/* The rail. Numbered because the order is real — an address belongs
										    to a patient, a service to a city, a package to a service — and a
										    list of open questions in no particular order is what made this
										    page feel like homework. */}
										<div className="space-y-5">
											<DecisionStep
												index={1}
												label={t(
													"drafts.paste_wa_form.decisions.patient.label",
												)}
												state={stepState("patient")}
												summary={stepSummary("patient")}
												editLabel={t("drafts.paste_wa_form.decisions.edit")}
												closeLabel={t("drafts.paste_wa_form.decisions.close")}
												onClose={
													editingStep ? () => setEditingStep(null) : undefined
												}
												onEdit={() => setEditingStep("patient")}
											>
												<FieldGrid
													fields={STEP_FIELDS.patient ?? []}
													data={preview.data}
													format={formatField}
												/>

												<RadioGroup
													value={patientChoiceValue(decisions.patient)}
													onValueChange={(value) =>
														decide((current) => choosePatient(current, value))
													}
												>
													<CandidateList>
														{patientCandidates.map((candidate) => (
															<OptionRow
																key={candidate.patientId}
																value={`existing:${candidate.patientId}`}
																id={`patient-${candidate.patientId}`}
															>
																<div className="flex items-baseline justify-between gap-2">
																	<p className="font-medium">
																		{candidate.name}
																	</p>

																	<span className="text-xs shrink-0 text-muted-foreground tabular-nums">
																		{candidate.patientNumber}
																	</span>
																</div>

																{/* The score as a bar rather than a pill: how sure the matcher
																    is reads off the fill without anyone parsing a number, and it
																    is one boxed element fewer in a row that carries several. The
																    bar is out of the reading order — the figure beside it says
																    the same thing. */}
																<div className="flex items-center gap-2">
																	<Progress
																		aria-hidden
																		className="w-20 h-1"
																		value={candidate.score * 100}
																	/>

																	<span className="text-xs text-muted-foreground tabular-nums">
																		{t("drafts.paste_wa_form.decisions.score", {
																			score: percent(candidate.score),
																		})}
																	</span>
																</div>

																<p className="text-xs text-muted-foreground">
																	{[
																		candidate.contactPhone,
																		candidate.dateOfBirth,
																		candidate.activeAddress?.locationCity,
																	]
																		.filter(Boolean)
																		.join(" • ")}
																</p>

																{/* Why the matcher thinks so, in its own words — a score alone
									    is not something an admin can check. */}
																{candidate.reasons?.length ? (
																	<div className="flex flex-wrap gap-1 pt-0.5">
																		{candidate.reasons.map((reason) => (
																			<Badge
																				key={reason}
																				variant="accent"
																				className="px-1.5 py-0 font-normal rounded-full"
																			>
																				{reason}
																			</Badge>
																		))}
																	</div>
																) : null}
															</OptionRow>
														))}
													</CandidateList>

													<OptionRow value={NEW_CHOICE} id="patient-new">
														<p className="font-medium">
															{t("drafts.paste_wa_form.decisions.patient.new")}
														</p>
														<p className="text-xs text-muted-foreground">
															{patientCandidates.length
																? t(
																		"drafts.paste_wa_form.decisions.patient.new_hint",
																	)
																: t(
																		"drafts.paste_wa_form.decisions.patient.empty",
																	)}
														</p>
													</OptionRow>
												</RadioGroup>
											</DecisionStep>

											<DecisionStep
												index={2}
												label={t(
													"drafts.paste_wa_form.decisions.address.label",
												)}
												state={stepState("address")}
												summary={stepSummary("address")}
												waitingText={t(
													"drafts.paste_wa_form.decisions.address.needs_patient",
												)}
												editLabel={t("drafts.paste_wa_form.decisions.edit")}
												closeLabel={t("drafts.paste_wa_form.decisions.close")}
												onClose={
													editingStep ? () => setEditingStep(null) : undefined
												}
												onEdit={() => setEditingStep("address")}
											>
												<FieldGrid
													fields={STEP_FIELDS.address ?? []}
													data={preview.data}
													format={formatField}
												/>

												{/* Addresses belong to a patient, so there is nothing to offer until one
									    is chosen — and choosing another one re-reads with that patient, so
									    these candidates are always the selected patient's own. */}
												{decisions.patient ? (
													<RadioGroup
														value={addressChoiceValue(decisions.address)}
														onValueChange={(value) =>
															decide((current) => chooseAddress(current, value))
														}
													>
														<CandidateList>
															{addressCandidates.map((candidate) => (
																<OptionRow
																	key={candidate.patientAddressId}
																	value={`existing:${candidate.patientAddressId}`}
																	id={`address-${candidate.patientAddressId}`}
																>
																	<p className="font-medium text-pretty">
																		{candidate.address}
																	</p>

																	<p className="text-xs text-muted-foreground">
																		{[
																			candidate.locationCity,
																			candidate.postalCode,
																		]
																			.filter(Boolean)
																			.join(" • ")}
																	</p>

																	<div className="flex flex-wrap gap-1 pt-0.5">
																		{/* A missing distance is its own state: neither side had
									    coordinates, which is not "0 km away". */}
																		<Badge
																			variant="secondary"
																			className="px-1.5 py-0 font-normal rounded-full"
																		>
																			{candidate.distanceKm === null
																				? t(
																						"drafts.paste_wa_form.decisions.address.distance_unknown",
																					)
																				: t(
																						"drafts.paste_wa_form.decisions.address.distance",
																						{
																							km: candidate.distanceKm.toFixed(
																								1,
																							),
																						},
																					)}
																		</Badge>

																		{(candidate.reasons ?? []).map((reason) => (
																			<Badge
																				key={reason}
																				variant="accent"
																				className="px-1.5 py-0 font-normal rounded-full"
																			>
																				{reason}
																			</Badge>
																		))}
																	</div>
																</OptionRow>
															))}
														</CandidateList>

														<OptionRow value={NEW_CHOICE} id="address-new">
															<p className="font-medium">
																{t(
																	"drafts.paste_wa_form.decisions.address.new",
																)}
															</p>
															<p className="text-xs text-muted-foreground">
																{addressCandidates.length
																	? t(
																			"drafts.paste_wa_form.decisions.address.new_hint",
																		)
																	: t(
																			"drafts.paste_wa_form.decisions.address.empty",
																		)}
															</p>
														</OptionRow>
													</RadioGroup>
												) : (
													<p className="text-xs text-muted-foreground">
														{t(
															"drafts.paste_wa_form.decisions.address.needs_patient",
														)}
													</p>
												)}
											</DecisionStep>

											{/* Only a new address needs a city picked; an existing one already carries
									    its own — which is why this step is absent rather than settled. */}
											{decisions.address?.mode === "new" ? (
												<DecisionStep
													index={3}
													label={t("drafts.paste_wa_form.decisions.city.label")}
													state={stepState("city")}
													summary={stepSummary("city")}
													waitingText={t(
														"drafts.paste_wa_form.decisions.waiting",
													)}
													editLabel={t("drafts.paste_wa_form.decisions.edit")}
													closeLabel={t("drafts.paste_wa_form.decisions.close")}
													onClose={
														editingStep ? () => setEditingStep(null) : undefined
													}
													onEdit={() => setEditingStep("city")}
												>
													<FieldGrid
														fields={STEP_FIELDS.city ?? []}
														data={preview.data}
														format={formatField}
													/>

													{cityCandidates.length ? (
														<RadioGroup
															value={
																decisions.locationId === null
																	? ""
																	: String(decisions.locationId)
															}
															onValueChange={(value) =>
																decide((current) => ({
																	...current,
																	locationId: Number(value),
																}))
															}
														>
															{/* Every match is offered, ambiguous ones included — "Bekasi" is
									    both a city and a regency, and the page does not get to pick. */}
															{cityCandidates.map((option) => (
																<OptionRow
																	key={option.id}
																	value={String(option.id)}
																	id={`city-${option.id}`}
																>
																	<p className="font-medium">{option.city}</p>
																	<p className="text-xs text-muted-foreground">
																		{option.state}
																	</p>
																</OptionRow>
															))}
														</RadioGroup>
													) : (
														<p className="text-xs text-muted-foreground">
															{t("drafts.paste_wa_form.decisions.city.empty")}
														</p>
													)}
												</DecisionStep>
											) : null}

											{/* One stop, two controls. The service is offered only once the city is
										    known and only where it is actually delivered; the package belongs to
										    that service. Splitting them made the admin cross a numbered boundary
										    in the middle of a single choice. */}
											<DecisionStep
												index={stepIndex("service")}
												label={t(
													"drafts.paste_wa_form.decisions.service_package.label",
												)}
												state={stepState("service")}
												summary={`${stepSummary("service")} · ${stepSummary("package")}`}
												// This stop has four distinct reasons for not being reachable yet,
												// and the one that applies is more use than "waiting its turn".
												waitingText={
													serviceDisabledReason ??
													t("drafts.paste_wa_form.decisions.waiting")
												}
												editLabel={t("drafts.paste_wa_form.decisions.edit")}
												closeLabel={t("drafts.paste_wa_form.decisions.close")}
												onClose={
													editingStep ? () => setEditingStep(null) : undefined
												}
												onEdit={() => setEditingStep("service")}
											>
												<FieldGrid
													fields={STEP_FIELDS.service ?? []}
													data={preview.data}
													format={formatField}
												/>

												{/* Each control gets its own block with padding above it. The step already
												    stacks its children, but that gap is tuned for one control per step — two
												    of them plus the reading above left the labels sitting on the box edges. */}
												<div className="pt-2 space-y-2">
													<Label className="uppercase" htmlFor="wa-service">
														{t("drafts.paste_wa_form.decisions.service.label")}
													</Label>

													{/* Kept out of a <Deferred>: its fallback would replace the control
											    with a skeleton, and Inertia's router loads the prop either way. */}
													<Select
														disabled={!!serviceDisabledReason}
														value={
															decisions.serviceId === null
																? ""
																: String(decisions.serviceId)
														}
														onValueChange={(value) =>
															decide((current) => ({
																...current,
																serviceId: Number(value),
																// Packages belong to a service, so the one picked
																// under the previous service cannot survive.
																packageId: null,
															}))
														}
													>
														<SelectTrigger id="wa-service">
															<SelectValue
																placeholder={t(
																	"drafts.paste_wa_form.decisions.service.placeholder",
																)}
															/>
														</SelectTrigger>

														<SelectContent>
															{serviceOptions.map((option) => (
																<SelectItem
																	key={option.id}
																	value={String(option.id)}
																>
																	{option.name}
																</SelectItem>
															))}
														</SelectContent>
													</Select>

													<p className="text-xs text-muted-foreground text-pretty">
														{serviceDisabledReason ??
															t("drafts.paste_wa_form.decisions.service.hint")}
													</p>
												</div>
												<div className="pt-2 space-y-2">
													<Label className="uppercase" htmlFor="wa-package">
														{t("drafts.paste_wa_form.decisions.package.label")}
													</Label>

													{/* Scoped to the chosen service by the backend, so "8x" in the text can
									    only ever be linked to a package that service actually sells. */}
													<Select
														value={
															decisions.packageId === null
																? ""
																: String(decisions.packageId)
														}
														onValueChange={(value) =>
															decide((current) => ({
																...current,
																packageId: Number(value),
															}))
														}
													>
														<SelectTrigger id="wa-package">
															<SelectValue
																placeholder={t(
																	"drafts.paste_wa_form.decisions.package.placeholder",
																)}
															/>
														</SelectTrigger>

														<SelectContent>
															{packages.map((option) => (
																<SelectItem
																	key={option.id}
																	value={String(option.id)}
																>
																	<span className="flex items-center gap-2">
																		{option.name} ({option.numberOfVisit}x)
																		{option.numberOfVisit ===
																			requestedVisits && (
																			<Badge
																				variant="accent"
																				className="px-1.5 py-0 font-normal rounded-full"
																			>
																				{t(
																					"drafts.paste_wa_form.decisions.package.matches_text",
																				)}
																			</Badge>
																		)}
																	</span>
																</SelectItem>
															))}
														</SelectContent>
													</Select>

													<p className="text-xs text-muted-foreground text-pretty">
														{!packages.length
															? t(
																	"drafts.paste_wa_form.decisions.package.empty",
																)
															: requestedVisits === null
																? t(
																		"drafts.paste_wa_form.decisions.package.hint",
																	)
																: visitMatched
																	? t(
																			"drafts.paste_wa_form.decisions.package.hint_matched",
																			{ visits: requestedVisits },
																		)
																	: t(
																			"drafts.paste_wa_form.decisions.package.hint_unmatched",
																			{ visits: requestedVisits },
																		)}
													</p>
												</div>
											</DecisionStep>

											{/* Template line 8 is required, so the condition is an admin decision
											    rather than something a blank line can skip past. */}
											<DecisionStep
												index={stepIndex("condition")}
												last
												label={t(
													"drafts.paste_wa_form.decisions.condition.label",
												)}
												state={stepState("condition")}
												summary={stepSummary("condition")}
												waitingText={t(
													"drafts.paste_wa_form.decisions.waiting",
												)}
												editLabel={t("drafts.paste_wa_form.decisions.edit")}
												closeLabel={t("drafts.paste_wa_form.decisions.close")}
												onClose={
													editingStep ? () => setEditingStep(null) : undefined
												}
												onEdit={() => setEditingStep("condition")}
											>
												<FieldGrid
													fields={STEP_FIELDS.condition ?? []}
													data={preview.data}
													format={formatField}
												/>

												<div className="pt-2 space-y-2">
													<Label className="uppercase" htmlFor="wa-condition">
														{t(
															"drafts.paste_wa_form.decisions.condition.label",
														)}
													</Label>

													<Select
														value={decisions.condition ?? ""}
														onValueChange={(value) =>
															decide((current) => ({
																...current,
																condition: value,
															}))
														}
													>
														<SelectTrigger id="wa-condition">
															<SelectValue
																placeholder={t(
																	"drafts.paste_wa_form.decisions.condition.placeholder",
																)}
															/>
														</SelectTrigger>

														<SelectContent>
															{WA_CONDITIONS.map((option) => (
																<SelectItem
																	key={option.code}
																	value={option.code}
																>
																	{conditionLabel(option.code)}
																</SelectItem>
															))}
														</SelectContent>
													</Select>

													<p className="text-xs text-muted-foreground text-pretty">
														{decisions.condition === "BELUM DIINFORMASIKAN"
															? t(
																	"drafts.paste_wa_form.decisions.condition.not_informed",
																)
															: t(
																	"drafts.paste_wa_form.decisions.condition.hint",
																)}
													</p>
												</div>
											</DecisionStep>
										</div>

										{/* Not a decision the draft waits on — the admin pasting is already
										    its PIC. This only adds the people who should see it too, which is
										    why it sits off the rail: nothing here can hold the save button. */}
										<div className="pt-2 space-y-2 border-t">
											{/* Above the multi-select, same order as the booking form: the
											    slots say who is accountable per stage, the list below says
											    who else should see the draft. */}
											{draftPicKpiEnabled && (
												<PicSlotSelectors
													className="pb-2"
													admins={picSlotAdmins}
													value={picSlots}
													onChange={setPicSlots}
												/>
											)}

											<Label className="uppercase" htmlFor="wa-admin-pics">
												{draftPicKpiEnabled
													? t(
															"drafts.paste_wa_form.decisions.pics.supporting_label",
														)
													: t("drafts.paste_wa_form.decisions.pics.label")}
											</Label>

											{/* The list is deferred, so the picker waits on it rather than
											    rendering an empty box that looks like "no colleagues". */}
											<Deferred
												data={["admins"]}
												fallback={<Skeleton className="w-full h-9" />}
											>
												{adminOptions.length ? (
													<MultiSelector
														values={adminPicIds}
														onValuesChange={setAdminPicIds}
														maxShows={4}
														// The values are the ids the save sends, so the
														// chips need telling how to read.
														labelFor={(id) =>
															adminOptions.find((admin) => admin.id === id)
																?.name ?? id
														}
													>
														<MultiSelectorTrigger>
															<MultiSelectorInput
																id="wa-admin-pics"
																placeholder={t(
																	"drafts.paste_wa_form.decisions.pics.placeholder",
																)}
															/>
														</MultiSelectorTrigger>

														<MultiSelectorContent>
															<MultiSelectorList>
																{adminOptions.map((admin) => (
																	<MultiSelectorItem
																		key={admin.id}
																		value={admin.id}
																	>
																		<div className="min-w-0">
																			<p className="font-medium">
																				{admin.name}
																			</p>
																			<p className="text-xs break-words text-muted-foreground">
																				{admin.email}
																			</p>
																		</div>
																	</MultiSelectorItem>
																))}
															</MultiSelectorList>
														</MultiSelectorContent>
													</MultiSelector>
												) : (
													<p className="text-xs text-muted-foreground">
														{t("drafts.paste_wa_form.decisions.pics.empty")}
													</p>
												)}
											</Deferred>

											<p className="text-xs text-muted-foreground text-pretty">
												{draftPicKpiEnabled
													? t(
															"drafts.paste_wa_form.decisions.pics.supporting_hint",
														)
													: t("drafts.paste_wa_form.decisions.pics.hint")}
											</p>
										</div>
									</CardContent>
								</Card>

								{/* What the template said that no decision rests on — medical history,
								    the maps link, where the patient heard about us. Folded away rather
								    than dropped: it is read once in a while, and four cards of it below
								    every decision was most of the page's length for none of its work. */}
								<Collapsible>
									<Card>
										<CollapsibleTrigger className="flex items-center justify-between w-full gap-2 p-4 text-left rounded-lg group focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
											<CardTitle className="text-sm uppercase">
												{t("drafts.paste_wa_form.rest.title")}
											</CardTitle>

											<ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
										</CollapsibleTrigger>

										<CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
											<CardContent className="pt-0 space-y-4">
												{[
													...REST_GROUPS,
													{ key: "other", fields: REST_OTHER_FIELDS },
												].map((group) =>
													// A group the parser returned nothing for would print a heading
													// over an empty grid, so an empty remainder prints nothing.
													group.fields.length ? (
														<div key={group.key} className="space-y-2">
															<div className="flex items-center justify-between gap-2">
																<p className="text-xs font-medium uppercase text-muted-foreground">
																	{t(
																		`drafts.paste_wa_form.rest.groups.${group.key}`,
																	)}
																</p>

																{/* These values are the ones an admin most often finds wrong
																    and least often needs a decision re-answered for, and the
																    text that produced them is a long monospace block two
																    columns away. The button is a way back into it, not a way
																    to type over the reading: the text stays the source. */}
																<Button
																	type="button"
																	variant="ghost"
																	size="sm"
																	className="h-6 px-2 text-xs"
																	onClick={editSourceText}
																>
																	{t("drafts.paste_wa_form.source.edit")}
																</Button>
															</div>

															<FieldGrid
																fields={group.fields}
																data={preview.data}
																format={formatField}
															/>
														</div>
													) : null,
												)}
											</CardContent>
										</CollapsibleContent>
									</Card>
								</Collapsible>
							</>
						) : null}
					</div>
				</div>
			</PageContainer>
		</>
	);
}
